import { Observable, of as observableOf, Subscription } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Yii } from "libraries/interfaces/Yii";
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { FormService } from 'libraries/service/form/form.service';
import { HotelService } from 'libraries/hotel/services/hotel.service';
import { ViewService } from 'libraries/hotel/services/view.service';
import { BreadcrumbService } from 'libraries/hotel/services/breadcrumb.service';
import { SearchService } from 'libraries/hotel/services/search.service';
import { IHotel } from 'libraries/hotel/models/hotel';
import { IView } from 'libraries/hotel/models/view';
import { ISearchRequest } from 'libraries/service/resources/hotel/ISearchParameters';
import { SharedData } from 'libraries/hotel/shared-data';
import { MESSAGES } from 'libraries/hotel/messages';
import { ICurrencies, ISearchOffersResponse } from "libraries/service/resources/hotel/ISearchResponse";
import { SearchHotelFormComponent } from "libraries/hotel/containers/search-hotel-form/search-hotel-form.component";
import { IConfigSharedHotel } from 'libraries/hotel/models/config-shared-hotel';
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { IDescriptionLabels } from "libraries/module/formbuilder/fields/form-field/components/field-description/field-description.component";
import { DATE_EXPRESSION_DETAILS, DATE_EXPRESSION_FORM_INTRO } from "libraries/date/date-shared-wording";
import { ILabels } from "libraries/interfaces/ILabels";
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { RuntimeError } from "libraries/service/error-tracing/runtime-error.model";
import { HotelsListComponent } from 'libraries/hotel/containers/hotels-list/hotels-list.component';

// #TODO 30-04-2018_FG Terminer les interfaces

@Component({
    selector: 'search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css']
})
export class SearchComponent implements OnInit, OnDestroy {
    @ViewChild('searchHotelForm')
    public searchHotelForm: SearchHotelFormComponent;

    @ViewChild('hotelsListView')
    public hotelsListView: HotelsListComponent;

    public hotelsList: IHotel[] = [];
    public view: IView;

    public error: INotification = {
        type: NotificationType.Error,
        active: false,
        message: null
    };

    public request: ISearchRequest;
    public quotation;
    public currency: ICurrencies;
    public isValid: boolean = true;
    public configSharedHotel: IConfigSharedHotel;

    public readonly labels: ILabels = {
        staySearch: Yii.t('wui-iqbh-js', 'Recherche d\'hébergement'),
        searchSummary: Yii.t('wui-iqbh-js', 'Résumé de votre recherche'),
        backToSummary: Yii.t('wui-iqbh-js', 'Retour au résumé'),
        editSearch: Yii.t('wui-iqbh-js', 'Modifier la recherche'),
        search: Yii.t('wui-iqbh-js', 'Rechercher')
    };

    public readonly formDescription: IDescriptionLabels = {
        main: DATE_EXPRESSION_FORM_INTRO,
        detailList: DATE_EXPRESSION_DETAILS
    };
    public showDescription: boolean = false;

    private subscription = new Subscription();

    constructor(
        private formService: FormService,
        private hotelService: HotelService,
        private viewService: ViewService,
        private searchService: SearchService,
        private breadcrumbService: BreadcrumbService,
        private sharedData: SharedData,
        private activatedRoute: ActivatedRoute,
        private changeDetector: ChangeDetectorRef,
        private readonly errorTracingService: ErrorTracingService
    ) { }

    public ngOnInit(): void {
        this.subscription.add(
            this.viewService.getView().subscribe((view) => {
                this.view = view;
                // Otherwise there is an error stating that changes
                // have been made after change detection
                this.changeDetector.detectChanges();
            })
        );

        this.subscription.add(
            this.activatedRoute.data.pipe(
                map((resolverData) => resolverData.configSharedHotel))
                .subscribe((configSharedHotel: IConfigSharedHotel) => {
                    this.configSharedHotel = configSharedHotel;
                })
        );

        let showMap: string = 'list';

        // Get optionals params
        this.subscription.add(
            this.activatedRoute.queryParams.pipe(map((params: Params) => params.view))
                .subscribe((param) => showMap = param)
        );
        // Get trip key
        const tripObs = this.activatedRoute.params.pipe(
            mergeMap((params) => {
                if (params && params.trips) {
                    // Start loader
                    this.viewService.changeView({
                        ...this.view,
                        loaders: {
                            ...this.view.loaders,
                            searchHotels: true
                        }
                    });

                    if (showMap) {
                        // Change view
                        this.viewService.changeView({
                            loaders: {
                                ...this.view.loaders
                            },
                            searchForm: false,
                            resume: true,
                            list: showMap === 'list',
                            map: showMap === 'map'
                        });
                    }

                    return this.searchService.getHotelsFromExistingTripsKey(params.trips, showMap);
                } else {
                    this.viewService.changeView({
                        searchForm: true,
                        resume: false,
                        hotelsList: false,
                        loaders: {
                            ...this.view.loaders,
                        }
                    });
                }
                // If no trips return null
                return observableOf(null);
            }))
            // Then form hotels list
            .subscribe((tripPropertyQuote: IHotel[]) => {
                this.currency = this.hotelService.currencies;
                if (tripPropertyQuote) {
                    this.hotelSubscription(tripPropertyQuote);
                }
            });

        this.subscription.add(tripObs);

        // Set new values to breadcrumb
        this.changeBreadcrumb();

        // On value changes in search form
        this.listenSearchFormChanges();

        // Get data from shared data modules
        this.subscription.add(
            this.sharedData.getData('request')
                .subscribe((request) => this.request = request)
        );
        this.subscription.add(
            this.sharedData.getData('quotation')
                .subscribe((quotation) => this.quotation = quotation)
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public switchView(): void {
        this.viewService.changeView({
            ...this.view,
            searchForm: !this.view.searchForm,
            resume: !this.view.resume
        });
    }

    public search(): void {
        this.searchHotelForm.submit(this.searchHotelForm.form);
    }

    public onFormSubmitted(request: ISearchRequest): void {
        this.sharedData.setData('request', request);
        this.request = request;
    }

    public select(data): void {
        //
    }

    public onValidityChanges(isValid: boolean) {
        this.isValid = isValid;
    }

    public toggleDescription(): void {
        this.showDescription = !this.showDescription;
    }

    private listenSearchFormChanges(): void {
        this.subscription.add(
            this.formService.formData$.pipe(
                tap((request: ISearchRequest) => {
                    this.error.active = false;

                    this.viewService.changeView({
                        ...this.view,
                        loaders: {
                            ...this.view.loaders,
                            searchHotels: true
                        }
                    });

                    this.breadcrumbService.setItems({
                        search: {
                            active: true,
                            disabled: false
                        },
                        quotation: {
                            active: false,
                            disabled: true
                        },
                        booking: {
                            active: false,
                            disabled: true
                        }
                    });

                    this.hotelService.setRequestToCache(request);
                    this.hotelService.setTravellersDistribution(request);
                }),
                switchMap((request: ISearchRequest): Observable<IHotel[]> => this.searchHotel(request).pipe(
                    catchError((error) => {
                        return observableOf(null);
                    }),
                    mergeMap((response: ISearchOffersResponse) =>
                        this.searchService.formatHotelsData(response).pipe(
                            catchError((error) => {
                                this.errorTracingService.captureException(new RuntimeError("Error during search result formatting", error));
                                return observableOf(null);
                            }))))
                ),
                catchError((error: Error) => {
                    this.error = {
                        type: NotificationType.Error,
                        active: true,
                        message: MESSAGES.getHotelsListError + error.message
                    };

                    return observableOf(null);
                }))
                .subscribe((hotels: IHotel[]) => this.hotelSubscription(hotels))
        );
    }

    private searchHotel(request: ISearchRequest): Observable<ISearchOffersResponse> {
        this.viewService.changeView({
            searchForm: false,
            resume: true,
            hotelsList: false,
            loaders: {
                ...this.view.loaders,
                searchHotels: true
            }
        });

        return this.hotelService.getHotelsList(request).pipe(
            tap((response: ISearchOffersResponse) => {
                this.error.active = false;
                this.hotelService.setCurrencies(response.currency);
                this.currency = this.hotelService.currencies;
            }),
            catchError((error: Error) => {
                this.error = {
                    type: NotificationType.Error,
                    active: true,
                    message: MESSAGES.getHotelsListError + error.message
                };

                return observableOf(null);
            }));
    }

    private hotelSubscription(tripProperyQuote: IHotel[]): void {
        this.viewService.changeView({
            ...this.view,
            loaders: {
                ...this.view.loaders,
                searchHotels: false
            }
        });

        if (tripProperyQuote && tripProperyQuote.length > 0) {
            this.request = this.hotelService.getRequestFromCache();
            this.viewService.changeView({
                searchForm: false,
                resume: true,
                hotelsList: true,
                loaders: {
                    ...this.view.loaders,
                    searchHotels: false
                }
            });

            this.hotelsList = tripProperyQuote;
        } else {
            this.error = {
                type: NotificationType.Warning,
                active: true,
                message: MESSAGES.searchHotelsWarning
            };
        }
    }

    private changeBreadcrumb(): void {
        this.breadcrumbService.setItems({
            search: {
                active: true,
                disabled: false
            },
            quotation: {
                active: false,
            },
            booking: {
                active: false,
            }
        });
    }
}
